export const hasVirtualViewsElement = (data: any) => {
  if (data.virtual_views?.length > 0) {
    return true;
  }
  return data.children_short?.some((child: any) => child.virtual_views?.length > 0);
};

/**
 * Gallery Modal V2 tab to open for 3D views.
 * Uses the dedicated "3D" media tab when it would exist; otherwise the first
 * room type name that has virtual_views (room-type section in the modal).
 */
export const getGalleryTabFor3DViews = (data: any, roomTypesData: any[] = []) => {
  const rooms = roomTypesData?.length ? roomTypesData : data?.children_short || [];
  const roomVirtualViewIds = new Set(
    rooms
      .flatMap((room: any) => (room?.virtual_views || []).map((view: any) => view?.id))
      .filter(Boolean),
  );

  const hasDedicated3DTab = (data?.virtual_views || []).some(
    (view: any) => view?.id && !roomVirtualViewIds.has(view.id),
  );
  if (hasDedicated3DTab) return "3D";

  const roomWith3D = rooms.find((room: any) => room?.virtual_views?.length > 0);
  return roomWith3D?.name || "3D";
};

export const hasVideoElement = (data: any) => {
  if (data.videos?.length > 0) {
    return true;
  }
  return data.children_short?.some((child: any) => child.videos?.length > 0);
};

export const videoElementWithoutYoutube = (data: any) => {
  if (
    data.videos?.length > 0 &&
    data.videos.filter((v: any) => !v?.path?.includes("youtube") && v?.type).length
  ) {
    return data.videos.filter((v: any) => !v?.path?.includes("youtube") && v?.type);
  }
  if (data.children_short?.some((child: any) => child.videos?.length > 0)) {
    return data.children_short?.reduce((acc: any, child: any) => {
      if (child.videos?.length > 0) {
        return acc.concat(
          child.videos.filter((v: any) => !v?.path?.includes("youtube") && v?.type),
        );
      }
      return acc;
    }, []);
  }
  return [];
};

export const sortVideosWithLiveTourFirst = (videos: any[]) => {
  if (!videos?.length) return [];
  const liveTourVideos = videos.filter((v: any) => v?.type === "live_tour_guided");
  const otherVideos = videos.filter((v: any) => v?.type !== "live_tour_guided");
  return [...liveTourVideos, ...otherVideos];
};

export const allVideoElementWithoutYoutube = (data: any) => {
  const videos = [];
  if (data.videos?.length > 0) {
    videos.push(
      ...data.videos.filter(
        (v: any) => !v?.path?.includes("youtube") && v?.type !== "vo_tour" && v?.type,
      ),
    );
  }
  if (data.children_short?.some((child: any) => child.videos?.length > 0)) {
    const childVideos = data.children_short?.reduce((acc: any, child: any) => {
      if (child.videos?.length > 0) {
        return acc.concat(
          child.videos.filter(
            (v: any) => !v?.path?.includes("youtube") && v?.type !== "vo_tour" && v?.type,
          ),
        );
      }
      return acc;
    }, []);
    videos.push(...childVideos);
  }

  return sortVideosWithLiveTourFirst(videos);
};

const getPlayKey = (url: any, autoPlay: boolean) => {
  if (autoPlay && url?.hostname === "live.tourdash.com") {
    return "show";
  }
  return "play";
};

export const getVirtualViewSrc = (virtualView: any, autoPlay = true) => {
  try {
    const src = virtualView?.id;
    if (!window) return src;
    const url = new URL(src);
    const searchParams = new URLSearchParams(url?.search);
    const playKey = getPlayKey(url, autoPlay);
    searchParams.set(playKey, autoPlay ? "1" : "0");
    url.search = searchParams.toString();
    return url.toString();
  } catch (error) {
    return "";
  }
};
export const VIDEO_THUMBNAIL_DIMENSIONS: any = { height: 300, width: 300 };

const getYoutubeVideoHash = (path: any) => {
  const videoHashArray = path.split("v=");
  return videoHashArray[1].split("&")[0];
};
export const getYoutubeVideoSrc = (path: any) => {
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = path?.match(regExp);

  return match && match[2].length === 11 ? "https://www.youtube.com/embed/".concat(match[2]) : "";
};

export const getYoutubeVideoThumbUrl = (path: any) => {
  const videoHash = getYoutubeVideoHash(path);
  return `https://i3.ytimg.com/vi/${videoHash}/mqdefault.jpg`;
};

const getCloudianryThumbUrl = (thumbUrl: string, { height = 100, width = 100 }) => {
  const arr = thumbUrl?.split("/") || [];
  arr[6] = height ? `b_auto,c_fill_pad,g_auto,h_${height},w_${width}` : `c_fill,w_${width},so_9`;

  return arr.join("/");
};

const getGumletThumbnail = (
  thumbUrl: string,
  { width: defaultWidth, height: defaultHeight } = VIDEO_THUMBNAIL_DIMENSIONS,
) => {
  if (!thumbUrl) return "";

  const url = new URL(thumbUrl);
  const { searchParams } = url;

  // Try to get dimensions from URL parameters first
  const urlWidth = searchParams.get("w") || searchParams.get("width");
  const urlHeight = searchParams.get("h") || searchParams.get("height");

  // Use dimensions in this order:
  // 1. URL parameters if present
  // 2. Provided dimensions
  // 3. VIDEO_THUMBNAIL_DIMENSIONS
  const width = urlWidth ? parseInt(urlWidth, 10) : defaultWidth;
  const height = urlHeight ? parseInt(urlHeight, 10) : defaultHeight;

  // Set the thumbnail parameters
  url.searchParams.set("thumbnail-width", width.toString());
  url.searchParams.set("thumbnail-height", height.toString());
  url.searchParams.set("thumbnail-mode", "fill");
  url.searchParams.set("thumbnail-fill", "cover");

  return url.toString();
};

export const getVideoSrc = (video: any, w = 720) => {
  if (video?.path?.includes("youtube")) {
    return getYoutubeVideoSrc(video?.path);
  }

  return video?.platform === "gumlet"
    ? video?.path
    : `https://res.cloudinary.com/amberstudent/video/upload/w_${w},c_fit,q_auto/${video?.path}`;
};

export const getVideoThumb = (video: any, dimensions = VIDEO_THUMBNAIL_DIMENSIONS) => {
  if (video?.path?.includes("youtube")) {
    return getYoutubeVideoThumbUrl(video?.path);
  }

  return video?.platform === "gumlet"
    ? getGumletThumbnail(video?.thumbnail_url, dimensions)
    : getCloudianryThumbUrl(video?.thumbnail_url, dimensions);
};
export const getFeaturedImage = (images: any) => {
  images = images || [];
  let featuredImage = images?.[0];
  images.forEach((image: any) => {
    if (image?.featured?.toString() == "true") {
      featuredImage = image;
    }
  });
  return featuredImage;
};
export const getAllImages = (data: any) => {
  if (!data) return null;
  const images = [...data?.images];
  const featuredImage = getFeaturedImage(data?.images);
  data.children_short?.forEach((child: any) => {
    child.images?.forEach((image: any) => {
      if (!featuredImage || featuredImage.base_path !== image.base_path) {
        images.push({ ...(image || {}), level: "child" });
      }
    });
  });
  return images;
};
export const getItemsByCategories = (data: any) => {
  const images = getAllImages(data);
  const itemsByCategories: any = {
    all: images,
  };

  const videos = data?.videos?.length > 0 ? [...data.videos] : [];
  data?.children_short?.forEach((child: any) => {
    if (child.videos?.length > 0) {
      videos.push(...child.videos);
    }
  });
  if (videos.length > 0) {
    itemsByCategories["videos"] = sortVideosWithLiveTourFirst(videos);
  }

  const virtualViews = data?.virtual_views?.length > 0 ? [...data.virtual_views] : [];
  data?.children_short?.forEach((child: any) => {
    if (child.virtual_views?.length > 0) {
      virtualViews.push(...child.virtual_views);
    }
  });
  if (virtualViews.length > 0) {
    itemsByCategories["3D"] = virtualViews;
  }

  images?.forEach((image) => {
    const type = image.type ?? "others";
    if (!itemsByCategories[type]) {
      itemsByCategories[type] = [image];
    } else {
      itemsByCategories[type].push(image);
    }
  });

  if (itemsByCategories["all"]?.length === itemsByCategories["others"]?.length) {
    delete itemsByCategories["others"];
  }

  if (itemsByCategories["all"]?.length === 0) {
    delete itemsByCategories["all"];
  }

  return itemsByCategories;
};
export const getOverViewImages = (galleryItems: any) => {
  const overviewItems = Object.keys(galleryItems)
    .filter(
      (key) =>
        ![
          "all",
          "floor_plan",
          "3D",
          "videos",
          "common_area",
          "exterior",
          "shot_by_residents",
          "site_plan",
        ]?.includes(key),
    )
    .map((key) => getFeaturedImage(galleryItems[key]));

  return overviewItems.length ? overviewItems : galleryItems["all"];
};
